<template>
  <div>
    <div class="lang-status" :style="gridColumns">
      <span
        v-for="(translation, lang) in originalTranslations"
        :key="lang"
        class="lang-status-link"
        :class="getStatusColor(translation.status)"
        :title="lang"
        @click="show(lang)"
      >
        <span></span>
      </span>
    </div>

    <v-dialog
      v-model="showDialog"
      max-width="75%"
    >
      <v-card>
        <v-card-title>
          <span class="headline">Translations: {{ title }} ({{ id }})</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col
                cols="4"
                v-for="(translation, lang) in editedTranslations"
                :key="lang"
              >
                <!-- style pointer-events - hook for show icon title -->
                <v-textarea
                  auto-grow
                  rows="1"
                  style="pointer-events: initial"
                  v-model="translation.text"
                  :label="lang"
                  outlined
                  dense
                  :disabled="!editable"
                  :hide-details="true"
                  :ref="lang"
                >
                  <template v-slot:append>
                    <v-icon
                      :title="getStatusLabel(translation.status)"
                      :color="getStatusColor(translation.status)"
                    >mdi-alert-octagon</v-icon>
                  </template>
                </v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            text
            @click="showDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            v-if="editable"
            color="blue darken-1"
            text
            @click="save(true)"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog
      :is-open.sync="dialogs.warningDialog.isOpened"
      width="400"
      confirm-button-text="Save"
      :enter-to-confirm="false"
      @confirm="save(false).finally(() => { closeDialog('warningDialog') })"
    >
      <template v-slot:title>
        Warning
      </template>
      <template v-slot:body>
        <div v-for="(item, lang) in dialogs.warningDialog.data.warning" :key="lang">
          <div>You are trying to enter invalid character(s) for {{ lang }} script. Proceed?</div>
          <table class="statistics_table">
            <tr>
              <td>№</td>
              <td>Character</td>
              <td>Unicode number</td>
              <td>HTML-code</td>
            </tr>
            <tr v-for="(char, key) in item" :key="key">
              <td>{{ parseInt(key) + 1 }}</td>
              <td>{{ char }}</td>
              <td>{{ getCodeHex(char) }}</td>
              <td>#{{ char.charCodeAt(0) }}</td>
            </tr>
          </table>
        </div>
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import _debounce from 'lodash.debounce';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import manageDialogsMixin from '../../mixins/manageDialogsMixin';
import { getCachedTranslationStatuses } from './TranslationStatusesCache';

export default {
  name: 'TranslationsBar',
  components: { ConfirmDialog },
  mixins: [
    manageDialogsMixin,
  ],
  props: {
    title: {
      type: String,
      default: '',
    },
    id: {
      type: Number,
      default: 0,
    },
    model: {
      type: String,
      default: '',
    },
    translations: {
      type: Object,
      default() {
        return {};
      },
    },
    initData: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    popup: {
      type: Boolean,
      default: true,
    },
    triggerUpdate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showDialog: false,
      originalTranslations: {},
      editedTranslations: {},
      translationStatuses: {},
      dialogs: {
        warningDialog: {
          isOpened: false,
          data: {
            warning: {},
          },
          defaults: {
            warning: {},
          },
        },
      },
    };
  },
  created() {
    getCachedTranslationStatuses()
      .then((response) => {
        this.translationStatuses = response.data;
      });

    if (this.initData) {
      this.getTranslations();
    }
    if (Object.keys(this.translations).length) {
      this.originalTranslations = this.translations;
    }

    this.getDebouncedTranslations = _debounce(this.getTranslations, 500);
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    getStatusLabel(status) {
      return this.translationStatuses[status] ? this.translationStatuses[status].text : '';
    },

    getStatusColor(status) {
      return this.translationStatuses[status] ? this.translationStatuses[status].color : '';
    },

    getTranslations() {
      this.$requestHelpers.post(
        'api/admin/translations',
        {
          model: this.model,
          id: this.id,
        },
        {
          withOverlay: false,
        },
      ).then((response) => {
        this.originalTranslations = response.data.result;
      });
    },

    show(lang) {
      if (!this.popup) {
        return;
      }

      this.editedTranslations = JSON.parse(JSON.stringify(this.originalTranslations));
      this.showDialog = true;
      setTimeout(() => {
        this.$refs[lang][0].$refs.input.focus();
      }, 200);
    },

    save(checkParams = true) {
      this.showDialog = false;
      const changedTranslations = Object.keys(this.editedTranslations)
        // eslint-disable-next-line max-len
        .filter((lang) => this.editedTranslations[lang].text !== this.originalTranslations[lang].text)
        .reduce((cur, lang) => Object.assign(cur, { [lang]: this.editedTranslations[lang] }), {});

      return this.$requestHelpers.post(
        'api/admin/save-translations',
        {
          model: this.model,
          id: this.id,
          translations: changedTranslations,
          checkParams,
        },
      ).then((response) => {
        Object.assign(this.originalTranslations, response.data.result.data);
        if (response.data.result.error) {
          this.setSnack({
            color: 'red',
            text: response.data.result.error,
          });
        }
        if (response.data.result.warning) {
          this.openDialog(
            'warningDialog',
            { warning: response.data.result.warning },
          );
        }
      });
    },

    getCodeHex(char) {
      const code = char.charCodeAt(0);
      let codeHex = code.toString(16).toUpperCase();
      while (codeHex.length < 4) {
        codeHex = `0${codeHex}`;
      }
      return `U+${codeHex};`;
    },
  },
  computed: {
    gridColumns() {
      return `grid-template-columns: repeat(${Math.ceil(Object.keys(this.originalTranslations).length / 2)}, 8px);`;
    },
  },
  watch: {
    translations(newValue) {
      this.originalTranslations = newValue;
    },
    triggerUpdate() {
      this.getDebouncedTranslations();
    },
  },
};
</script>

<style scoped>
  .lang-status {
    line-height: 5px;
    max-width: 280px;
  }
  .lang-status-link {
    cursor: pointer;
    width: 8px;
    height: 4px;
    display: inline-block;
    margin: 0 3px 0 0;
  }
  .statistics_table {
    border:none;
  }
  .statistics_table td{
    border: 1px solid black;
    padding:6px;
    font-size:12px;
  }
  .statistics_table tr:nth-child(odd) td {
    background:#e1e1e1;
  }
  .statistics_table tr td:first-child {
    background:#FFE4B5;
    white-space:nowrap;
  }
  .statistics_table tr:first-child td {
    background:#b0e0e6;
  }
</style>
